<template>
  <form @submit.prevent="handleSubmit" data-testid="acoes-marketing-form">
    <div class="form-field">
      <input-date
        elem-id="acaoMarketingData"
        v-model="formData.acao_data"
        :label="t('Data')"
        ng-required
        data-testid="acoes-marketing-data-input"
      />
      <small v-if="errors.acao_data" class="error-message" data-testid="acoes-marketing-data-error">{{ errors.acao_data }}</small>
    </div>

    <div class="form-field">
      <input-text
        elem-id="acaoMarketingNome"
        v-model="formData.acao_nome"
        :label="t('Nome da ação')"
        type="text"
        required
        fluid
        data-testid="acoes-marketing-nome-input"
      />
      <small v-if="errors.acao_nome" class="error-message" data-testid="acoes-marketing-nome-error">{{ errors.acao_nome }}</small>
    </div>

    <div class="form-field">
      <select-list
        elem-id="acaoMarketingTipo"
        v-model="formData.acao_tipo"
        :label="t('Tipo')"
        :itens-select="tipoOptions"
        :placeholder="t('Selecione o tipo')"
        :auto-select-first="false"
        data-testid="acoes-marketing-tipo-select"
      />
      <small v-if="errors.acao_tipo" class="error-message" data-testid="acoes-marketing-tipo-error">{{ errors.acao_tipo }}</small>
    </div>

    <div class="form-field">
      <franchise-select
        elem-id="acaoMarketingFranquiaId"
        v-model="formData.acao_franquia_id"
        :label="t('Franquia')"
        :placeholder="t('Selecione a franquia')"
        :disabled="franquiaLocked"
        data-testid="acoes-marketing-franquia-select"
      />
      <small v-if="errors.acao_franquia_id" class="error-message" data-testid="acoes-marketing-franquia-error">{{ errors.acao_franquia_id }}</small>
    </div>

    <div class="form-field">
      <input-text
        elem-id="acaoMarketingLink"
        v-model="formData.acao_link"
        :label="linkExigido ? t('Link da publicação') + ' *' : t('Link')"
        :placeholder="linkExigido ? 'https://www.instagram.com/p/…' : ''"
        type="url"
        fluid
        data-testid="acoes-marketing-link-input"
      />
      <small v-if="errors.acao_link" class="error-message" data-testid="acoes-marketing-link-error">{{ errors.acao_link }}</small>
      <small v-else-if="linkExigido" class="field-hint" data-testid="acoes-marketing-link-hint">{{ t('Copie a URL da publicação. É ela que conta a ação no Programa de Excelência.') }}</small>
    </div>

    <div class="form-field">
      <input-text-area
        elem-id="acaoMarketingDescricao"
        v-model="formData.acao_descricao"
        :label="t('Descrição')"
        data-testid="acoes-marketing-descricao-input"
      />
    </div>

    <h4>{{ t('Fotos') }} ({{ galeria.length }}/{{ GALERIA_MAX }})</h4>
    <div v-for="(foto, idx) in galeria" :key="idx" class="repeatable-row" :data-testid="`acoes-marketing-galeria-row-${idx}`">
      <acoes-marketing-photo-picker
        v-model="foto.foto_url"
        :test-id-prefix="`acoes-marketing-galeria-${idx}`"
      />
      <input-text :elem-id="`acaoMarketingGaleriaAlt${idx}`" v-model="foto.foto_alt" :label="t('Legenda')" type="text" fluid :data-testid="`acoes-marketing-galeria-alt-input-${idx}`" />
      <Button
        icon="pi pi-trash"
        size="small"
        class="p-button-outlined p-button-danger"
        :label="t('Remover')"
        :data-testid="`acoes-marketing-galeria-remove-${idx}`"
        @click="removeGaleriaFoto(idx)"
      />
    </div>
    <Button
      icon="pi pi-plus"
      size="small"
      class="p-button-outlined"
      :label="t('Adicionar foto')"
      :disabled="galeria.length >= GALERIA_MAX"
      data-testid="acoes-marketing-galeria-add"
      @click="addGaleriaFoto"
    />
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from '../../inputs/InputText.vue';
import InputTextArea from '../../inputs/InputTextArea.vue';
import InputDate from '../../inputs/InputDate.vue';
import SelectList from '../../inputs/SelectList.vue';
import FranchiseSelect from '../../inputs/FranchiseSelect.vue';
import AcoesMarketingPhotoPicker from './AcoesMarketingPhotoPicker.vue';
import { useAcoesMarketing, ACOES_MARKETING_TIPOS, ACOES_MARKETING_TIPOS_EXIGEM_LINK } from '../../composables/useAcoesMarketing';
import { emitValidationFailed } from '../../composables/useFormValidation';
import { permissionsService, permissionsState } from '../../services/permissionsService';
import type { AcoesMarketing_SalvarInput } from '../../contracts/AcoesMarketing_SalvarInput';
import { eventBus } from '../../eventBus';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type AcaoId = number | string | null;

const GALERIA_MAX = 10;

const props = withDefaults(defineProps<{
  acaoId?: AcaoId;
  isEditMode?: boolean;
}>(), {
  acaoId: null,
  isEditMode: false
});

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
  'validation-failed': [];
}>();

const { t } = useI18n();
const { fetchAcao, salvarAcao } = useAcoesMarketing();

const acaoIdNumber = computed<number | null>(() => {
  if (!props.acaoId) return null;
  return typeof props.acaoId === 'string' ? parseInt(props.acaoId, 10) : props.acaoId;
});

interface FormShape {
  acao_data: string;
  acao_nome: string;
  acao_tipo: string;
  acao_franquia_id: number | string | null;
  acao_link: string;
  acao_descricao: string;
}

interface GaleriaFotoRow {
  foto_url: string;
  foto_alt: string;
}

/** Franchises the signed-in user is linked to (permissionsState is already user-scoped). */
const userFranquiaIds = (): number[] => Object.keys(permissionsState.franquias ?? {}).map(Number).filter(n => Number.isFinite(n) && n > 0);

/** Non-Full users with a single franchise get it preselected and locked. */
const isFull = computed<boolean>(() => permissionsService.getUserGroups().includes('Full'));
const franquiaLocked = computed<boolean>(() => !isFull.value && userFranquiaIds().length === 1);

const buildEmptyForm = (): FormShape => ({
  acao_data: '',
  acao_nome: '',
  acao_tipo: '',
  acao_franquia_id: franquiaLocked.value ? userFranquiaIds()[0] : null,
  acao_link: '',
  acao_descricao: ''
});

const formData = reactive<FormShape>(buildEmptyForm());
const galeria = reactive<GaleriaFotoRow[]>([]);

type ErrorKey = 'acao_data' | 'acao_nome' | 'acao_tipo' | 'acao_franquia_id' | 'acao_link';
const errors = reactive<Record<ErrorKey, string>>({ acao_data: '', acao_nome: '', acao_tipo: '', acao_franquia_id: '', acao_link: '' });

const isLoading = ref<boolean>(false);

const tipoLabels = computed<Record<string, string>>(() => ({
  influencer: t('Influencer'),
  acao_social: t('Ação social / sustentabilidade'),
  evento: t('Evento'),
  midia_paga: t('Mídia paga'),
  parceria: t('Parceria'),
  outro: t('Outro')
}));

const linkExigido = computed<boolean>(() => ACOES_MARKETING_TIPOS_EXIGEM_LINK.includes(formData.acao_tipo));

const tipoOptions = computed(() => ACOES_MARKETING_TIPOS.map(id => ({ ID: id, Name: tipoLabels.value[id] })));

const addGaleriaFoto = (): void => {
  if (galeria.length >= GALERIA_MAX) return;
  galeria.push({ foto_url: '', foto_alt: '' });
};

const removeGaleriaFoto = (idx: number): void => {
  galeria.splice(idx, 1);
};

const loadForEdit = async (): Promise<void> => {
  if (!acaoIdNumber.value) {
    logWarn('AcoesMarketingForm', 'loadForEdit', 'No acaoId provided');
    return;
  }

  log('AcoesMarketingForm', 'loadForEdit', 'Loading action for edit', acaoIdNumber.value);
  isLoading.value = true;

  try {
    const acao = await fetchAcao(acaoIdNumber.value);
    if (!acao) {
      throw new Error('Dados da ação não encontrados');
    }

    formData.acao_data = acao.acao_data || '';
    formData.acao_nome = acao.acao_nome || '';
    formData.acao_tipo = acao.acao_tipo || '';
    formData.acao_franquia_id = acao.acao_franquia_id ?? null;
    formData.acao_link = acao.acao_link || '';
    formData.acao_descricao = acao.acao_descricao || '';

    galeria.splice(0, galeria.length, ...((acao.galeria || []).map(f => ({
      foto_url: f.foto_url || '',
      foto_alt: f.foto_alt || ''
    }))));

    clearErrors();
  } catch (err) {
    const error = err as Error;
    logError('AcoesMarketingForm', 'loadForEdit', 'Error loading action', { acaoId: acaoIdNumber.value, error: error.message });
    eventBus.emit('globalMessage.error', t('Erro ao carregar ação') + ': ' + error.message);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

const isValidUrl = (value: string): boolean => {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

const validateForm = (): boolean => {
  log('AcoesMarketingForm', 'validateForm', 'Validating form');
  clearErrors();
  let isValid = true;

  if (!formData.acao_data || formData.acao_data.trim() === '') {
    errors.acao_data = t('Campo obrigatório');
    isValid = false;
  }
  if (!formData.acao_nome || formData.acao_nome.trim() === '') {
    errors.acao_nome = t('Campo obrigatório');
    isValid = false;
  }
  if (!formData.acao_tipo) {
    errors.acao_tipo = t('Campo obrigatório');
    isValid = false;
  }
  const franquiaId = Number(formData.acao_franquia_id);
  if (!Number.isFinite(franquiaId) || franquiaId <= 0) {
    errors.acao_franquia_id = t('Campo obrigatório');
    isValid = false;
  }
  if (linkExigido.value && formData.acao_link.trim() === '') {
    errors.acao_link = t('Cole a URL da publicação');
    isValid = false;
  } else if (formData.acao_link && formData.acao_link.trim() !== '' && !isValidUrl(formData.acao_link.trim())) {
    errors.acao_link = t('Informe uma URL válida (http:// ou https://)');
    isValid = false;
  }

  if (!isValid) {
    emitValidationFailed('AcoesMarketingForm', errors);
  }
  log('AcoesMarketingForm', 'validateForm', 'Validation result', isValid);
  return isValid;
};

const clearErrors = (): void => {
  (Object.keys(errors) as ErrorKey[]).forEach(k => { errors[k] = ''; });
};

const resetForm = (): void => {
  log('AcoesMarketingForm', 'resetForm', 'Resetting form');
  Object.assign(formData, buildEmptyForm());
  galeria.splice(0, galeria.length);
  clearErrors();
};

const buildPayload = (): AcoesMarketing_SalvarInput => {
  const payload: AcoesMarketing_SalvarInput = {
    acao_franquia_id: Number(formData.acao_franquia_id),
    acao_data: formData.acao_data,
    acao_nome: formData.acao_nome.trim(),
    acao_tipo: formData.acao_tipo,
    // The generated type says undefined[] (nested InputContract not expanded);
    // the real shape is AcoesMarketing_GaleriaFotoInput[].
    galeria: galeria.filter(f => f.foto_url && f.foto_url.trim() !== '') as unknown as AcoesMarketing_SalvarInput['galeria']
  };
  if (formData.acao_link.trim() !== '') {
    payload.acao_link = formData.acao_link.trim();
  }
  if (formData.acao_descricao.trim() !== '') {
    payload.acao_descricao = formData.acao_descricao.trim();
  }
  if (props.isEditMode && acaoIdNumber.value) {
    payload.id = acaoIdNumber.value;
  }
  return payload;
};

const handleSubmit = async (): Promise<void> => {
  log('AcoesMarketingForm', 'handleSubmit', 'Form submitted');

  if (!validateForm()) {
    logWarn('AcoesMarketingForm', 'handleSubmit', 'Validation failed');
    emit('validation-failed');
    return;
  }

  isLoading.value = true;
  try {
    const payload = buildPayload();
    await salvarAcao(payload);
    eventBus.emit('globalMessage.success', props.isEditMode ? t('Ação atualizada com sucesso') : t('Ação criada com sucesso'));
    emit('submit-success');
    resetForm();
  } catch (err) {
    const error = err as Error;
    logError('AcoesMarketingForm', 'handleSubmit', 'Error submitting form', error);
    eventBus.emit('globalMessage.error',
      (props.isEditMode ? t('Erro ao atualizar ação') : t('Erro ao criar ação')) + ': ' + error.message
    );
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.acaoId, async (newId) => {
  if (newId && props.isEditMode) {
    await nextTick();
    await loadForEdit();
  }
});

onMounted(() => {
  if (props.isEditMode && acaoIdNumber.value) {
    loadForEdit();
  }
});

defineExpose({
  loadForEdit,
  handleSubmit,
  resetForm,
  validateForm
});
</script>

<style scoped>
.form-field { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; }
.error-message { color: var(--p-red-500); font-size: 0.875rem; margin-top: 0.25rem; display: block; }
.field-hint { color: var(--p-surface-500); font-size: 0.8rem; display: block; }
.repeatable-row { display: flex; align-items: flex-end; gap: 0.5rem; margin-bottom: 1rem; padding-bottom: 1rem; border-bottom: 1px solid var(--p-surface-200); }
</style>
